import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ThemeMode = "light" | "dark" | "system";
export type Theme = "light" | "dark";

interface ThemeStore {
    mode: ThemeMode;
    theme: Theme;
    setMode: (mode: ThemeMode) => void;
    toggleTheme: () => void;
    initializeTheme: () => void;
}

/** Adds or removes the `dark` class on <html> */
const applyThemeToDocument = (theme: Theme) => {
    if (theme === "dark") {
        document.documentElement.classList.add("dark");
    } else {
        document.documentElement.classList.remove("dark");
    }
};

/** Reads the OS-level color scheme preference */
const getSystemTheme = (): Theme => {
    if (typeof window !== "undefined" && window.matchMedia) {
        return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return "light";
};

const resolveTheme = (mode: ThemeMode): Theme => {
    if (mode === "system") return getSystemTheme();
    return mode;
};

export const useThemeStore = create<ThemeStore>()(
    persist(
        (set, get) => ({
            mode: "light" as ThemeMode,
            theme: "light" as Theme,

            setMode: (mode: ThemeMode) => {
                const theme = resolveTheme(mode);
                set({ mode, theme });
                applyThemeToDocument(theme);
            },

            toggleTheme: () => {
                const currentMode = get().mode;
                const order: ThemeMode[] = ["light", "dark", "system"];
                const nextIndex = (order.indexOf(currentMode) + 1) % order.length;
                get().setMode(order[nextIndex]);
            },

            initializeTheme: () => {
                const state = get();
                const theme = resolveTheme(state.mode);
                set({ theme });
                applyThemeToDocument(theme);
            },
        }),
        {
            name: "merchant-portal-theme",
            onRehydrateStorage: () => (state) => {
                if (state?.mode) {
                    const theme = resolveTheme(state.mode);
                    state.theme = theme;
                    applyThemeToDocument(theme);
                } else {
                    state?.setMode("light");
                }
            },
            skipHydration: false,
        },
    ),
);
